import {
  type ClaimInput,
  ENTITY_KIND_LABELS,
  type EntityKind,
  type FieldDef,
  IDENTITY_STATUSES,
  type IdentityStatus,
  isEntityKind,
  resolveProfile,
  type SchemaSnapshot,
  suggestCode,
  transliterate,
  typeChain,
  validateFieldValue,
} from '@hoopoe/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { DataText } from '../components/data/data-text';
import { Field } from '../components/guidance/field';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { PageHeader } from '../components/layout/app-shell';
import { EditorError } from '../components/schema/editor-kit';
import { draftToValue, FieldValueInput, RefSearchContext } from '../components/schema/field-input';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ApiError, api } from '../lib/api';
import { createEntity, DIRECTORY_TABS, useEntitySearch } from '../lib/entities';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Manual creation (task 9): what a person types is saved as claims they vouch for (`verified`, D66).

export const Route = createFileRoute('/entity/new')({
  validateSearch: (s: Record<string, unknown>): { kind?: EntityKind; type?: string; name?: string } => ({
    kind: isEntityKind(s.kind) ? s.kind : undefined,
    type: typeof s.type === 'string' ? s.type : undefined,
    name: typeof s.name === 'string' ? s.name : undefined,
  }),
  component: NewEntityPage,
});

export const IDENTITY_LABELS: Record<IdentityStatus, string> = {
  identified: 'Identified',
  probable: 'Probable',
  unidentified: 'Unidentified',
};

/** Languages offered for names (any BCP 47 code is accepted). */
export const LANGS = [
  ['en', 'English'],
  ['ar', 'Arabic'],
  ['fr', 'French'],
  ['de', 'German'],
  ['es', 'Spanish'],
  ['it', 'Italian'],
  ['nl', 'Dutch'],
  ['he', 'Hebrew'],
  ['fa', 'Persian'],
  ['tr', 'Turkish'],
  ['ru', 'Russian'],
  ['el', 'Greek'],
] as const;

/** Fields asked for at creation: the template's header block (Camp, Status…) or first block, plus required ones. */
export function creationFields(snap: SchemaSnapshot, typeId: string): FieldDef[] {
  const p = resolveProfile(snap, typeId);
  if (!p) return [];
  const header = p.blocks.find(b => b.layout === 'header' && b.fields.length) ?? p.blocks.find(b => b.fields.length);
  const all = [...p.blocks.flatMap(b => b.fields), ...p.otherGroups.flatMap(g => g.fields)];
  const picked = [...(header?.fields ?? p.otherGroups[0]?.fields ?? [])];
  for (const f of all) if (f.required && !picked.includes(f)) picked.push(f);
  return picked.filter(f => !f.archived && f.dataType !== 'table');
}

function NewEntityPage() {
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const canWrite = useCan('content.write');
  if (!snap) return <PageHeader title="New entity" description="Loading…" />;
  if (!canWrite)
    return (
      <>
        <PageHeader title="New entity" />
        <p className="text-sm text-muted-foreground">Only editors, reviewers and admins add entities.</p>
      </>
    );
  return <NewEntityForm snap={snap} kind={search.kind ?? 'actor'} typeKey={search.type} initialName={search.name ?? ''} />;
}

function NewEntityForm({
  snap,
  kind,
  typeKey,
  initialName,
}: {
  snap: SchemaSnapshot;
  kind: EntityKind;
  typeKey?: string;
  initialName: string;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tab = DIRECTORY_TABS.find(t => t.kind === kind);
  const types = snap.types
    .filter(t => t.kind === kind && !t.archived)
    .map(t => ({
      t,
      label: typeChain(snap.types, t.id)
        .reverse()
        .map(x => x.label)
        .join(' › '),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const initialType =
    types.find(x => x.t.key === typeKey)?.t ?? types.find(x => x.t.parentTypeId)?.t ?? types[0]?.t ?? snap.types.find(t => t.kind === kind);
  const [typeId, setTypeId] = useState(initialType?.id ?? '');
  const [name, setName] = useState(initialName);
  const [lang, setLang] = useState('');
  const [otherNames, setOtherNames] = useState('');
  const [code, setCode] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [identity, setIdentity] = useState<IdentityStatus>('identified');
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const fields = useMemo(() => (typeId ? creationFields(snap, typeId) : []), [snap, typeId]);
  const search = useEntitySearch(snap);
  const ids = { type: useId(), lang: useId(), names: useId(), code: useId(), identity: useId(), langs: useId() };

  const prefix = typeChain(snap.types, typeId).find(t => t.codePrefix)?.codePrefix ?? 'ENT_';
  const person = typeChain(snap.types, typeId).some(t => t.key === 'person');
  const suggested = name.trim() ? suggestCode(prefix, transliterate(name.trim()) ?? name.trim(), { person }) : `${prefix}…`;

  const submit = async () => {
    setError(null);
    const claims: ClaimInput[] = [];
    const errs: Record<string, string> = {};
    for (const f of fields) {
      const value = draftToValue(f, drafts[f.key]);
      const r = validateFieldValue(f, value, snap);
      if (!r.ok) errs[f.key] = r.errors.join(' ');
      else if (r.value !== null) claims.push({ predicate: f.key, value });
    }
    setErrors(errs);
    if (!name.trim()) return setError('A name is required.');
    if (Object.keys(errs).length) return setError('Check the highlighted fields.');
    setSaving(true);
    try {
      const e = await createEntity({
        typeId,
        name: name.trim(),
        lang: lang.trim() || null,
        names: otherNames
          .split('\n')
          .map(n => n.trim())
          .filter(Boolean),
        code: code.trim() || null,
        sensitivity: confidential ? 'confidential' : 'standard',
        identityStatus: identity,
        claims,
      });
      await qc.invalidateQueries({ queryKey: ['entities'] });
      // A new Case opens in its workspace, ready to have things filed in it.
      if (e.kind === 'case') navigate({ to: '/cases/$id', params: { id: e.id } });
      else navigate({ to: '/entity/$id', params: { id: e.id } });
    } catch (e) {
      if (e instanceof ApiError) {
        const suggestion = typeof e.data.suggestion === 'string' ? ` Try ${e.data.suggestion}.` : '';
        setError(((e.data.errors as string[] | undefined)?.join(' ') ?? e.message) + suggestion);
      } else setError('Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title={`New ${tab?.singular ?? 'entity'}`}
        description={`Add ${ENTITY_KIND_LABELS[kind].toLowerCase()} by hand. You can fill in the rest of the profile after saving.`}
      />
      <Tip id="entity-new" learnMore="add-and-edit-entities">
        What you type here is saved as verified, with you as its source. Search the directory first, so you don’t create a duplicate.
      </Tip>
      <form
        aria-label={`New ${tab?.singular ?? 'entity'}`}
        className="mt-4 max-w-3xl space-y-6"
        onSubmit={e => {
          e.preventDefault();
          void submit();
        }}
      >
        <section className="space-y-4 rounded-lg border bg-surface p-4">
          <h2 className="font-semibold">Identity</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <label htmlFor={ids.type} className="text-sm font-medium">
                  Type
                </label>
                <FieldHelp
                  label="Type"
                  help="Decides the profile layout, the fields, the color and the ID code prefix."
                  learnMore="schema-and-templates"
                />
              </div>
              <select
                id={ids.type}
                value={typeId}
                onChange={e => setTypeId(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
              >
                {types.map(({ t, label }) => (
                  <option key={t.id} value={t.id}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <Field
              guidance={{
                key: 'name',
                label: 'Name',
                required: true,
                help: 'The main name, as the entity writes it, in any script. Add other spellings below.',
                placeholder: kind === 'actor' ? 'e.g. Franc-Tireur or حسن حداد' : 'e.g. Meridian Summit 2024',
                learnMore: 'cross-language-names',
              }}
            >
              {control => <Input {...control} dir="auto" value={name} onChange={e => setName(e.target.value)} />}
            </Field>
            <SimilarNamesNotice kind={kind} name={name} />
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <label htmlFor={ids.lang} className="text-sm font-medium">
                  Language of the name
                </label>
                <FieldHelp
                  label="Language of the name"
                  help="A language code (en, ar, fr…). Optional: Hoopoe detects the script by itself."
                  example="ar"
                  learnMore="cross-language-names"
                />
              </div>
              <Input id={ids.lang} list={ids.langs} dir="ltr" value={lang} placeholder="e.g. fr" onChange={e => setLang(e.target.value)} />
              <datalist id={ids.langs}>
                {LANGS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <label htmlFor={ids.code} className="text-sm font-medium">
                  ID code
                </label>
                <FieldHelp
                  label="ID code"
                  help="Type prefix + name. Leave empty to use the suggestion (a number is added if it is taken)."
                  example="MED_FRANCTIREUR"
                  learnMore="claims-and-profiles"
                />
              </div>
              <Input
                id={ids.code}
                dir="ltr"
                className="font-mono"
                value={code}
                placeholder={suggested}
                onChange={e => setCode(e.target.value.toUpperCase())}
                aria-describedby={`${ids.code}-hint`}
              />
              <p id={`${ids.code}-hint`} className="text-xs text-muted-foreground">
                Suggested: <code>{suggested}</code>
              </p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center gap-1">
                <label htmlFor={ids.names} className="text-sm font-medium">
                  Other names
                </label>
                <FieldHelp
                  label="Other names"
                  help="Aliases, acronyms, transliterations and former names, one per line, in any script. Search finds the entity by any of them."
                  example="حسن حداد"
                  learnMore="cross-language-names"
                />
              </div>
              <textarea
                id={ids.names}
                dir="auto"
                value={otherNames}
                placeholder={'One per line, e.g.\nFT\nفرانك تيرور'}
                onChange={e => setOtherNames(e.target.value)}
                className="min-h-16 w-full rounded-md border border-input bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-1">
                <label htmlFor={ids.identity} className="text-sm font-medium">
                  Identity
                </label>
                <FieldHelp
                  label="Identity"
                  help="Identified: we know who it is. Probable: likely but not confirmed. Unidentified: described but unnamed (“an adviser to a Renew MEP”), to be merged later."
                  learnMore="core-concepts"
                />
              </div>
              <select
                id={ids.identity}
                value={identity}
                onChange={e => setIdentity(e.target.value as IdentityStatus)}
                className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
              >
                {IDENTITY_STATUSES.map(s => (
                  <option key={s} value={s}>
                    {IDENTITY_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-1 self-end pb-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={confidential} onChange={e => setConfidential(e.target.checked)} />
                <Lock className="size-3.5 text-muted-foreground" aria-hidden="true" /> Confidential
              </label>
              <FieldHelp
                label="Confidential"
                help="Handled by on-platform AI models only. Everyone invited still sees it. Only reviewers can lower it back to standard."
                learnMore="sensitivity-and-ai-routing"
              />
            </div>
          </div>
        </section>
        {fields.length > 0 && (
          <section className="space-y-4 rounded-lg border bg-surface p-4">
            <h2 className="font-semibold">Profile</h2>
            <RefSearchContext.Provider value={search}>
              <div className="grid gap-4 sm:grid-cols-2">
                {fields.map(f => (
                  <Field
                    key={f.key}
                    guidance={{
                      key: f.key,
                      label: f.label,
                      help: f.help ?? undefined,
                      placeholder: f.placeholder ?? undefined,
                      formatHint: f.formatHint ?? undefined,
                      required: f.required,
                      learnMore: 'field-types',
                    }}
                    error={errors[f.key]}
                  >
                    {control => (
                      <FieldValueInput
                        def={f}
                        lists={snap.lists}
                        value={drafts[f.key]}
                        onChange={v => setDrafts(d => ({ ...d, [f.key]: v }))}
                        control={control}
                      />
                    )}
                  </Field>
                ))}
              </div>
            </RefSearchContext.Provider>
          </section>
        )}
        <EditorError error={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={saving || !typeId}>
            {saving ? 'Saving…' : `Create ${tab?.singular ?? 'entity'}`}
          </Button>
          <Button type="button" variant="ghost" asChild>
            <Link to="/directory" search={{ kind }}>
              Cancel
            </Link>
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * Before a duplicate is made: entities of the same kind whose name matches what is typed, once accents, case and
 * punctuation are folded ("Inès Varga" finds "Ines Varga"). Shown as the name is typed; creating stays possible.
 */
function SimilarNamesNotice({ kind, name }: { kind: string; name: string }) {
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(name.trim()), 400);
    return () => clearTimeout(t);
  }, [name]);
  const found = useQuery({
    queryKey: ['entities', 'similar-names', kind, q],
    queryFn: () =>
      api<{ entities: { id: string; displayName: string; code: string }[] }>(
        `/entities?${new URLSearchParams({ q, kinds: kind, limit: '5' })}`,
      ),
    enabled: q.length >= 3,
    staleTime: 30_000,
  });
  const hits = q.length >= 3 ? (found.data?.entities ?? []) : [];
  if (!hits.length) return null;
  return (
    <div
      role="status"
      className="space-y-1 rounded-md border border-warning bg-surface p-3 text-sm sm:col-span-2"
      data-testid="similar-names"
    >
      <p className="font-medium">Already in the directory with a similar name:</p>
      <ul className="list-disc ps-5">
        {hits.map(e => (
          <li key={e.id}>
            <Link to="/entity/$id" params={{ id: e.id }} className="underline">
              <DataText>{e.displayName}</DataText>
            </Link>{' '}
            <span className="text-muted-foreground">{e.code}</span>
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground">Open it instead if it is the same one, or create a new one if it is someone else.</p>
    </div>
  );
}
